import { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { ConfigContext } from '../../../../contexts/ConfigContext';
import { parseDateLoose } from '../../../../utils/businessTime';
import { displayPhone } from '../../../../utils/validation/customerFields';
import './CancelBookingModal.css';

const modeOptions = [
  { value: 'admin', label: 'By Admin' },
  { value: 'customer', label: 'By customer' }
];

export default function CancelBookingModal({ show, onHide, booking, onSubmit, config: configProp }) {
  const configContext = useContext(ConfigContext);
  const config = configProp || configContext;
  const [mode, setMode] = useState(modeOptions[0].value);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  const formatDateReadable = (dateStr) => {
    if (!dateStr) return '-';
    // 'YYYY-MM-DD' read as a local date — new Date() would read it as UTC.
    const d = parseDateLoose(dateStr);
    if (!d || Number.isNaN(d.getTime())) return dateStr;
    const day = d.getDate();
    const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    const formattedDay = `${day}${suffix}`;
    return `${formattedDay} ${d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`;
  };

  useEffect(() => {
    if (show) {
      setMode(modeOptions[0].value);
      setReason('');
      setReasonError('');
    }
  }, [show]);

  const handleSubmit = () => {
    // The reason is optional; an empty one is sent as no reason.
    setReasonError('');
    if (onSubmit) {
      onSubmit({
        // bookingId: booking?.id || booking?.booking_id,
        mode,
        reason: reason.trim()
      });
    }
    onHide();
  };

  const isDark = config.mode === 'dark';
  const textColor = isDark ? '#e6e6e6' : '#000';
  const mutedColor = isDark ? '#a0a0a0' : '#555';
  const labelColor = isDark ? '#d0d0d0' : '#3a3a3a';

  return (
    <Modal show={show} onHide={onHide} centered size="md" className="cancel-modal" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Cancel Booking</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3 mb-3 align-items-center flex-wrap">
          <Col xs={12} sm="auto">
            <div className="cancel-value" style={{ color: textColor }}>
              {booking?.customer?.name || '-'}
            </div>
            <div className="cancel-subvalue" style={{ color: mutedColor }}>
              {displayPhone(booking?.customer?.phone) || '-'}
            </div>
          </Col>
          <Col xs={12} sm="auto">
            <div className="cancel-value" style={{ color: textColor }}>
              {formatDateReadable(booking?.booking_date)}
            </div>
            <div className="cancel-subvalue" style={{ color: mutedColor }}>
              {booking?.booking_time_formatted || booking?.time || '-'}
            </div>
          </Col>
          <Col xs={12} sm className="mt-2 mt-sm-0">
            <Form.Label className="cancel-label mb-1" style={{ color: labelColor }}>
              Mode of Cancellation
            </Form.Label>
            <Form.Select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              size="sm"
              style={{
                backgroundColor: isDark ? '#2a2a2a' : '#fff',
                color: textColor,
                borderColor: isDark ? '#444' : '#ced4da'
              }}
            >
              {modeOptions.map((opt) => (
                <option key={opt.value} value={opt.value} style={{ backgroundColor: isDark ? '#2a2a2a' : '#fff', color: textColor }}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <div className="mb-2 cancel-label" style={{ color: labelColor }}>
          Reason (optional):
        </div>
        <Form.Control
          as="textarea"
          rows={4}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (reasonError && e.target.value.trim() !== '') {
              setReasonError('');
            }
          }}
          placeholder="Add cancellation reason"
          isInvalid={!!reasonError}
          style={{
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            color: textColor,
            borderColor: reasonError ? '#dc3545' : isDark ? '#444' : '#ced4da'
          }}
        />
        <Form.Control.Feedback type="invalid" style={{ display: reasonError ? 'block' : 'none' }}>
          {reasonError}
        </Form.Control.Feedback>
      </Modal.Body>
      <Modal.Footer className="justify-content-center gap-3">
        <Button variant="outline-secondary" onClick={onHide} className="px-4">
          No
        </Button>
        <Button variant="primary" onClick={handleSubmit} className="px-4">
          Yes
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
